import React from 'react';
import type { FieldPath, UseFormReturn, FieldValues, PathValue } from 'react-hook-form';
import ConfirmationDialog from '../../../common/ConfirmationDialog/ConfirmationDialog';

export interface ConfirmModalProps {
  /**
   * Title of the modal
   */
  title: string;
  /**
   * Description of the modal
   */
  description: string;
  /**
   * Text of the confirm button
   */
  confirmText: string;
  /**
   * Text of the cancel button
   */
  cancelText: string;
  /**
   * Show the modal
   */
  show: boolean;
  /**
   * Callback to run on cancel
   */
  onCancel: () => void;
  /**
   * Callback to run on confirm
   */
  onConfirm: () => void;
}

interface ConfirmModalExtendedProps<DataT> extends ConfirmModalProps {
  fieldName: FieldPath<DataT>;
  formMethods: UseFormReturn<DataT>;
}

/**
 * Shows a confirmation dialog for a CheckField. On confirm, the field is set to true.
 *
 * @author Matt Vrydaghs
 */
const ConfirmModal = <DataT extends FieldValues>({
  fieldName,
  show,
  title,
  description,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  formMethods
}: ConfirmModalExtendedProps<DataT>): React.ReactElement => {
  return (
    <ConfirmationDialog
      data-testid="CheckFieldConfirmModal"
      aria-labelledby="check-field-confirm-modal"
      backdrop="static"
      show={show}
      submitButtonText={confirmText}
      cancelButtonText={cancelText}
      onSubmit={() => {
        formMethods.setValue(fieldName, true as PathValue<DataT, typeof fieldName>, {
          shouldTouch: true
        });
        onConfirm();
      }}
      onCancel={() => {
        formMethods.resetField(fieldName, {
          keepDirty: false,
          keepTouched: false,
          keepError: false,
          defaultValue: false
        });
        onCancel();
      }}
      titleText={title}
      bodyText={description}
    ></ConfirmationDialog>
  );
};

export default ConfirmModal;
